// Fully procedural gas giant shader. No textures — every band, eddy,
// vortex, festoon and limb glow is computed per fragment from a few
// dozen lines of GLSL. Used by the secret Nibiru body to demonstrate
// that we can match (and at zoom, exceed) the textured Jupiter look
// using only domain-warped FBM and OKLCh perceptual mixing.
//
// Reference techniques:
//   - Iñigo Quilez 2008, "Domain Warping" (the q/r warp pattern)
//   - Perlin / Stefan Gustavson simplex noise basis
//   - Bujack et al. 2025, OKLCh non-Riemannian colour interpolation
//   - Hubbard 1980 photometric Jupiter palette as colour seed
//
// The visual hierarchy:
//   1. Latitude bands (sin-modulated, ~12 bands across the disc) —
//      sets the alternating belt/zone macro structure.
//   2. Domain-warped turbulence in longitude — creates the festoons,
//      eddies, and shear lines that make the belts read as flowing
//      rather than striped.
//   3. A fixed "great vortex" SDF — a single major storm at a chosen
//      lat/long, swirled by polar angle so it spirals like the GRS.
//   4. Multi-octave FBM detail — adds the cloud noise that resolves
//      at any zoom level (the texture-free part is what gives this
//      shader infinite zoom detail).
//   5. Lambert + limb darkening + soft atmospheric edge glow.
//
// Performance: ~80 ALU ops per fragment, dominated by ~4 simplex
// noise lookups. Fine at 4K render targets on any modern GPU.

import { OKLAB_MIX_GLSL } from './oklab.js';

export const proceduralGasGiantVertexShader = `
  varying vec3 vLocalNormal;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;
  varying vec3 vLocalPos;

  void main() {
    vLocalNormal = normalize(normal);
    // True world-space normal. normalMatrix converts to VIEW space
    // (camera-relative), which made the lit hemisphere shift around
    // as the camera moved — the dark side tracked the viewer
    // instead of the sun. mat3(modelMatrix) is correct for
    // rotation + uniform scale, which is what every planet group
    // applies (axial tilt is a rotation, sphere scale is uniform).
    vWorldNormal = normalize(mat3(modelMatrix) * normal);
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    vLocalPos = position;
    // Camera-relative transform (modelViewMatrix is composed on the CPU in
    // double precision). Going through world space on the GPU loses the
    // vertices to float32 rounding far from the origin: at Neptune's
    // realistic distance (~150,000 units) positions snap to ~0.02 units,
    // the sphere cracks between triangles and the stars behind show through.
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const proceduralGasGiantFragmentShader = `
  precision highp float;

  varying vec3 vLocalNormal;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;
  varying vec3 vLocalPos;

  uniform float uTime;
  uniform vec3 uSunDir;          // unit vector, body -> sun in world space
  uniform vec3 uPaletteDeep;     // darkest band colour (linear sRGB)
  uniform vec3 uPaletteBelt;     // mid-warm belt colour
  uniform vec3 uPaletteZone;     // light zone colour
  uniform vec3 uPaletteStorm;    // great vortex core colour
  uniform float uBandCount;      // how many full band cycles across pole-to-pole
  uniform float uTurbulence;     // 0..1, amount of domain warp on band lookup
  uniform float uStormLatDeg;    // vortex centre latitude (degrees)
  uniform float uStormLonDeg;    // vortex centre longitude (degrees)
  uniform float uStormSize;      // angular radius of the vortex (radians)
  uniform float uLimbDarken;     // 0..1 strength of limb darkening curve
  uniform float uRimGlow;        // 0..1 atmospheric edge halo intensity
  uniform float uCloudDetailStrength; // multiplier on the high-freq cloud FBM amplitude
  uniform float uCloudDetailFreq;     // spatial frequency for the cloud FBM lookup
  uniform float uBandChaos;           // 0 = clean periodic sinusoidal bands; 1 = natural variation in band width, latitude meander, and per-band brightness
  uniform float uSunIntensity;        // per-planet sunlight multiplier (shared contract; <= 0.001 means unwired → treat as 1.0)

  ${OKLAB_MIX_GLSL}

  // Simplex 3D noise (Gustavson + Ashima Arts). Same primitive the sun
  // shader uses so the noise character matches the rest of the scene.
  vec3 _mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 _mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 _permute(vec4 x) { return _mod289(((x * 34.0) + 1.0) * x); }
  vec4 _taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) {
    const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
    const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
    vec3 i = floor(v + dot(v, C.yyy));
    vec3 x0 = v - i + dot(i, C.xxx);
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min(g.xyz, l.zxy);
    vec3 i2 = max(g.xyz, l.zxy);
    vec3 x1 = x0 - i1 + C.xxx;
    vec3 x2 = x0 - i2 + C.yyy;
    vec3 x3 = x0 - D.yyy;
    i = _mod289(i);
    vec4 p = _permute(_permute(_permute(
              i.z + vec4(0.0, i1.z, i2.z, 1.0))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0))
            + i.x + vec4(0.0, i1.x, i2.x, 1.0));
    float n_ = 0.142857142857;
    vec3 ns = n_ * D.wyz - D.xzx;
    vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_);
    vec4 x = x_ * ns.x + ns.yyyy;
    vec4 y = y_ * ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);
    vec4 b0 = vec4(x.xy, y.xy);
    vec4 b1 = vec4(x.zw, y.zw);
    vec4 s0 = floor(b0) * 2.0 + 1.0;
    vec4 s1 = floor(b1) * 2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));
    vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
    vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
    vec3 p0 = vec3(a0.xy, h.x);
    vec3 p1 = vec3(a0.zw, h.y);
    vec3 p2 = vec3(a1.xy, h.z);
    vec3 p3 = vec3(a1.zw, h.w);
    vec4 norm = _taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
    p0 *= norm.x; p1 *= norm.y; p2 *= norm.z; p3 *= norm.w;
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
  }

  // 5-octave fractal Brownian motion. Per octave the frequency doubles
  // and the amplitude halves; sum reads as 1/f noise.
  float fbm(vec3 p) {
    float v = 0.0;
    float amp = 0.5;
    for (int i = 0; i < 5; i++) {
      v += amp * snoise(p);
      p *= 2.02; // slight non-integer so octaves don't lock visually
      amp *= 0.5;
    }
    return v;
  }

  // Quilez-style double domain warp. The first warp shifts the lookup
  // point; the result of that lookup shifts a second one. Two warps
  // give the characteristic festoon / curl shape of Jupiter's bands
  // where a single warp would just look like wavy stripes.
  float warpedFbm(vec3 p, float warpAmount) {
    vec3 q = vec3(
      fbm(p + vec3(0.0, 0.0, 0.0)),
      fbm(p + vec3(5.2, 1.3, 0.0)),
      fbm(p + vec3(1.7, 9.2, 0.0))
    );
    vec3 r = vec3(
      fbm(p + warpAmount * q + vec3(1.7, 9.2, 0.0)),
      fbm(p + warpAmount * q + vec3(8.3, 2.8, 0.0)),
      fbm(p + warpAmount * q + vec3(3.5, 6.1, 0.0))
    );
    return fbm(p + warpAmount * r);
  }

  void main() {
    vec3 n = normalize(vLocalNormal);
    // Spherical coords from the local-space normal. Latitude is signed
    // (-PI/2 at south pole, +PI/2 at north). Longitude is computed
    // for storm-position math only — NEVER fed into the FBM warp or
    // cloud lookup, because atan(n.z, n.x) has a step discontinuity
    // at the antimeridian (z=0, x<0) and any noise driven by that
    // value produces a clearly visible meridian seam in the
    // procedural output. The fix: derive every noise sample point
    // from the 3D normal directly, which is continuous across the
    // antimeridian (no atan in the warp path means no seam).
    float lat = asin(clamp(n.y, -1.0, 1.0));
    float lon = atan(n.z, n.x);

    // Equatorial drift implemented as a Y-axis rotation of the
    // normal, scaled by cos²(lat) so the poles stay still and the
    // equator rotates max. The rotated normal becomes the noise
    // sample point — seamless because the unit sphere has no
    // discontinuities in 3D.
    float driftAngle = uTime * 0.012 * cos(lat) * cos(lat);
    float dc = cos(driftAngle);
    float ds = sin(driftAngle);
    vec3 nDrift = vec3(
      n.x * dc - n.z * ds,
      n.y,
      n.x * ds + n.z * dc
    );

    // Latitude band index, modulated by a warped FBM lookup. The
    // warp input is the drifted 3D normal scaled to set the feature
    // size — using 3D coordinates rather than the (lon, lat) pair
    // is what kills the antimeridian seam.
    vec3 warpInput = nDrift * 1.8 + vec3(0.0, 0.0, uTime * 0.03);
    float warpedBand = warpedFbm(warpInput, uTurbulence);
    float latWarped = lat + warpedBand * 0.06;

    // Natural-chaos layer. Pure sin(lat * N) bands read as too
    // mathematical — perfectly periodic widths, identical
    // amplitudes, uniform spacing. Real gas giants meander,
    // have narrow vs. wide bands, and some bands are deeper /
    // brighter than others. Three independent FBM lookups on the
    // 3D normal:
    //   bigEddies        → latitude meander (bands wiggle around)
    //   bandFreqMod      → band width variation (some thick, some thin)
    //   bandBrightnessMod → per-band amplitude (some pop, some soft)
    // All scaled by uBandChaos so Uranus/Neptune can keep clean
    // periodic bands while Nibiru gets the chaotic atmosphere read.
    vec3 chaosInput = nDrift * 1.45;
    float bigEddies         = fbm(chaosInput);
    float bandFreqMod       = fbm(chaosInput + vec3(7.4, 2.8, 1.1));
    float bandBrightnessMod = fbm(chaosInput + vec3(3.7, 1.2, 5.6));
    latWarped += bigEddies * 0.085 * uBandChaos;
    float chaosFreqMul = mix(1.0, 1.0 + bandFreqMod * 0.42, uBandChaos);

    float bandRaw = sin(latWarped * uBandCount * chaosFreqMul) * 0.5 + 0.5;
    // Per-band amplitude variation: some bands stay near 0.5
    // (subdued / hazy) while others swing fully to 0 or 1
    // (high-contrast belts/zones). Strength is the chaos amount.
    float bandStrength = mix(1.0, 0.55 + bandBrightnessMod * 0.55, uBandChaos);
    float band = mix(0.5, bandRaw, bandStrength);

    // Three-stop palette mix: deep → belt → zone. Two oklchMix calls
    // give a smooth perceptual transition through the warm reds without
    // the muddy mid-grey a straight linear-sRGB mix would produce.
    vec3 base = oklchMix(uPaletteDeep, uPaletteBelt, smoothstep(0.0, 0.5, band));
    base = oklchMix(base, uPaletteZone, smoothstep(0.4, 1.0, band));

    // High-frequency cloud detail. Same 3D-normal trick — pass the
    // drifted normal scaled up to pick the small-feature wavelength.
    // Procedural so it stays sharp at any zoom level, seamless
    // because the normal is continuous everywhere on the sphere.
    // Two layers at different frequencies stack a coarse "weather
    // system" overlay with finer cloud-streak structure so the
    // disc reads with the same multi-scale complexity as Jupiter's
    // texture — bands have macro-bulges plus micro-eddies, not
    // single-frequency noise.
    float cloud = fbm(nDrift * uCloudDetailFreq + vec3(uTime * 0.01));
    float fineDetail = fbm(nDrift * (uCloudDetailFreq * 2.6) + vec3(uTime * 0.018, uTime * 0.014, uTime * 0.022));
    base += cloud * 0.045 * uCloudDetailStrength;
    base += fineDetail * 0.025 * uCloudDetailStrength;

    // driftLon is still needed for the storm position math below
    // (which has its own wrap correction). Computing it AFTER the
    // warp lookup so the noise inputs above never touch it.
    float driftLon = lon + driftAngle;

    // The great vortex. Storm centre is uStormLatDeg / uStormLonDeg
    // (degrees, converted to radians). Angular distance from centre
    // gives the SDF; an ellipse stretch makes the storm wider than it
    // is tall, like the actual GRS aspect ratio.
    float stormLat = radians(uStormLatDeg);
    float stormLon = radians(uStormLonDeg);
    vec2 stormDelta = vec2(driftLon - stormLon, lat - stormLat);
    // Wrap longitude delta into [-PI, PI] so the storm doesn't appear
    // doubled at the antimeridian.
    if (stormDelta.x >  3.14159265) stormDelta.x -= 6.28318530;
    if (stormDelta.x < -3.14159265) stormDelta.x += 6.28318530;
    // Ellipse: stretch x more than y (storms are oblate east-west).
    float stormDist = length(stormDelta / vec2(1.8, 1.0));
    // Inside the storm, sample the swirl: polar angle around the
    // storm centre, plus radial distance, modulated by FBM so the
    // swirl reads as turbulent rather than a clean spiral.
    float stormAngle = atan(stormDelta.y, stormDelta.x);
    float swirl = sin(stormAngle * 3.0 + stormDist / uStormSize * 14.0 + uTime * 0.4);
    float stormPerturb = fbm(vec3(stormDelta * 10.0, uTime * 0.05));
    float stormMask = 1.0 - smoothstep(uStormSize * 0.7, uStormSize, stormDist);
    vec3 stormColor = oklchMix(
      uPaletteStorm,
      uPaletteBelt,
      0.4 + 0.35 * swirl + 0.3 * stormPerturb
    );
    base = mix(base, stormColor, stormMask);

    // Light it. Lambert from the sun direction in world space; limb
    // darkening tightens the falloff near the terminator so the disc
    // edge reads as fully shaded rather than a hard cosine cutoff.
    float NdotL = max(0.0, dot(normalize(vWorldNormal), normalize(uSunDir)));
    float lambert = pow(NdotL, mix(1.0, 0.55, uLimbDarken));

    // Day-side atmospheric rim. Fresnel-style edge brightness that
    // scales with the lit side so the rim glows on the day side and
    // smoothly fades to zero through the terminator.
    vec3 viewDir = normalize(cameraPosition - vWorldPos);
    float fresnel = pow(1.0 - max(0.0, dot(normalize(vWorldNormal), viewDir)), 3.0);
    float dayRim = fresnel * NdotL * uRimGlow;

    // Direct sunlight (banded daylight + sun-driven day rim) scales
    // with the per-planet sunlight multiplier; night-side limb and
    // ambient lift below stay unscaled.
    float sunI = uSunIntensity > 0.001 ? uSunIntensity : 1.0;
    vec3 lit = (base * lambert + uPaletteZone * dayRim * 0.4) * sunI;

    // Dark-side atmospheric limb only — a TIGHT fresnel ring at the
    // silhouette edge of the night hemisphere, no interior lift.
    // The previous interior back-glow was multiplying the body's
    // base colour (which carries the band pattern + cloud detail)
    // by 0.06 across the whole dark hemisphere, making the bright
    // bands clearly visible on the night side at ~25% brightness.
    // That read as "dark side isn't dark." Limiting the night
    // contribution to a thin limb (pow exponent 5 vs the original
    // 2.2) keeps the silhouette readable but lets the interior fall
    // to true black where it should.
    float nightFresnel = pow(1.0 - max(0.0, dot(normalize(vWorldNormal), viewDir)), 5.0);
    float nightBias = 1.0 - NdotL;
    lit += uPaletteBelt * nightFresnel * nightBias * 0.10;

    // Day-side ambient lift only. Kept on the lit hemisphere so
    // banding detail near the terminator reads through limb
    // darkening; no contribution at all on the night side.
    lit += base * 0.025 * NdotL;

    gl_FragColor = vec4(lit, 1.0);
  }
`;

// Uniforms are constructed by the body creator (e.g. nibiru.js) so
// this module stays free of a THREE.Vector3 import. The expected shape
// is documented in the fragment shader's `uniform` declarations above
// — match those names and types when wiring up the ShaderMaterial.
